import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createContext, useContextSelector } from 'use-context-selector';

export const useSetState = () => {
  const setState = useState(false)[1];
  return useCallback(() => setState((s) => !s), [setState]);
};

export type UseIntersectionObserverOpts = {
  /** unsubscribes from all observations after first intersection */
  lazy?: boolean;
  /** prevents state updates, use in conjunction with `useIntersectionObserverContext` subsciptions */
  callbackMode?: boolean;
} & Omit<IntersectionObserverInit, 'root'>;

type Entry = IntersectionObserverEntry;
type Subscription = (entry: Entry, observer: IntersectionObserver) => void;

/**
 * a hook which instantiates a single parent intersection observer for observing many children
 * intersecting with a viewport
 * @param opts - options to pass to the hook
 * @returns
 *  viewportRef - a callback ref to use on the viewport element
 *  ref - a callback ref to use on any children we want to observe in the viewport
 *  entries - a Map of all the elements being observed to their IntersectionObserverEntry
 *  changedEntries - a Map of only the elements whose entries have changed
 *  IOSubscriptionProvider - provides children with the IO subscription context
 */
export const useIntersectionObserver = (opts?: UseIntersectionObserverOpts) => {
  const setState = useSetState();
  const [root, setRoot] = useState<Element | null>(null);
  const ioRef = useRef<IntersectionObserver>(undefined);
  const prevRoot = useRef<Element | null>(null);
  const allEntries = useRef(new Map<Element, Entry | null>()).current;
  const changedEntries = useRef(new Map<Element, Entry>()).current;
  const subscriptions = useRef(new Map<Element, Subscription>()).current;
  const keyToElement = useRef(new Map<number, Element>()).current;
  const elementToKey = useRef(new Map<Element, number>()).current;
  const keyCount = useRef(0);

  const lazy = opts?.lazy;
  const callbackMode = opts?.callbackMode;
  const rootMargin = opts?.rootMargin;
  const threshold = opts?.threshold;

  const viewportRef = useCallback((el: Element) => {
    if (el && el !== prevRoot.current) {
      setRoot(el);
      prevRoot.current = el;
    }
  }, []);

  const observe = useCallback(
    (el: Element, subscription: Subscription) => {
      const io = ioRef.current;
      const key = keyCount.current;
      keyCount.current += 1;
      allEntries.set(el, null);
      keyToElement.set(key, el);
      elementToKey.set(el, key);
      if (subscription) subscriptions.set(el, subscription);
      if (io) io.observe(el);
      return key;
    },
    [allEntries, elementToKey, keyToElement, subscriptions]
  );

  const unobserve = useCallback(
    (key: number) => {
      const el = keyToElement.get(key);
      if (el) {
        const io = ioRef.current;
        if (io) io.unobserve(el);
        allEntries.delete(el);
        elementToKey.delete(el);
        subscriptions.delete(el);
        keyToElement.delete(key);
      }
    },
    [allEntries, elementToKey, keyToElement, subscriptions]
  );

  const ref: (
    subscription?: Subscription | undefined
  ) => (el: HTMLDivElement) => void | undefined = useCallback(
    (subscription?: Subscription) => {
      let key: number;
      return (el: HTMLDivElement) => {
        if (!el && typeof key === 'number') {
          unobserve(key);
        } else if (!allEntries.has(el) && subscription) {
          key = observe(el, subscription);
        }
      };
    },
    [allEntries, unobserve, observe]
  );

  /** create a new IO when the viewport changes */
  useEffect(() => {
    const callback: IntersectionObserverCallback = (entries, observer) => {
      const size = changedEntries.size;
      entries.forEach((entry) => {
        const prev = allEntries.get(entry.target);
        if (!prev || prev.isIntersecting !== entry.isIntersecting) {
          changedEntries.set(entry.target, entry);
          allEntries.set(entry.target, entry);
          if (callbackMode) {
            const subscription = subscriptions?.get(entry.target);
            if (subscription) subscription(entry, observer);
            if (lazy && entry.isIntersecting) observer.unobserve(entry.target);
          }
        }
      });

      if (changedEntries.size !== size) {
        if (!callbackMode) setState();
      }
    };

    const newIO = new IntersectionObserver(callback, {
      root,
      rootMargin,
      threshold,
    });

    allEntries.forEach((__, el) => newIO.observe(el));
    ioRef.current = newIO;

    return () => {
      newIO.disconnect();
    };
  }, [
    allEntries,
    callbackMode,
    changedEntries,
    lazy,
    root,
    rootMargin,
    setState,
    subscriptions,
    threshold,
  ]);

  /** call the subscriptions of changed elements */
  useEffect(() => {
    changedEntries.forEach((entry, el) => {
      const sub = subscriptions.get(el);
      const io = ioRef.current;
      const element: number | undefined = elementToKey.get(el);

      if (sub && io) sub(entry, io);

      if (lazy && io && entry.isIntersecting && element) {
        unobserve(element);
      }
      changedEntries.delete(el);
    });
  });

  /** provides a IntersectionObserverSubscriptionContext */
  const providerMemo = useMemo(() => ({ ref }), [ref]);
  const IOSubscriptionProvider = useCallback<React.FC<{ children: ReactNode }>>(
    ({ children }) => (
      <IntersectionObserverContext.Provider value={providerMemo}>
        {children}
      </IntersectionObserverContext.Provider>
    ),
    [providerMemo]
  );

  return {
    viewportRef,
    ref,
    entries: allEntries,
    changedEntries,
    IOSubscriptionProvider,
  };
};

const IntersectionObserverContext = createContext<
  Partial<Pick<ReturnType<typeof useIntersectionObserver>, 'ref'>>
>({
  ref: undefined,
});

/**
 * a context consumer for `useIntersectionObserver` observed elements
 * this will only re-render the element if its IO entry has changed
 *
 * @returns
 *  ref - a callback ref to use on a single element to be observed
 *  entry - the IntersectionObserverEntry for the elemnt
 */

type UseIntersectionObserverContextOptions = {
  /** how long (in ms) must the element be intersecting before triggering a change? */
  delay?: number;
  /**
   * called immediately when the IOEntry changes.
   **/
  callback?: (
    entry: IntersectionObserverEntry,
    observer: IntersectionObserver
  ) => void;
  /**
   * if true the `entry` state will not update. meant to be used in conjunction with `callback`
   */
  skipUpdate?: boolean;
};
export const useIntersectionObserverContext = (
  opts?: UseIntersectionObserverContextOptions
) => {
  const ioRef = useContextSelector(
    IntersectionObserverContext,
    (value) => value?.ref
  );
  const [entry, setEntry] = useState<IntersectionObserverEntry>();
  const [delayedIntersection, setDelayedIntersection] = useState(false);
  const delay = opts?.delay;

  const callbackRef = useRef(opts?.callback);
  callbackRef.current = opts?.callback;

  const skipUpdate = opts?.skipUpdate;
  const subscription = useCallback(
    (e: IntersectionObserverEntry, observer: IntersectionObserver) => {
      if (callbackRef.current) callbackRef?.current(e, observer);
      if (!skipUpdate && e) setEntry(e);
    },
    [skipUpdate]
  );
  const ref = useMemo(() => ioRef?.(subscription), [subscription, ioRef]);

  useEffect(() => {
    let delayTimer: number | undefined;
    if (delay && delay > 0) {
      if (entry?.isIntersecting) {
        delayTimer = window.setTimeout(() => {
          setDelayedIntersection(true);
        }, delay);
      } else {
        setDelayedIntersection(false);
      }
    } else {
      window.clearTimeout(delayTimer);
      setDelayedIntersection(false);
    }
    return () => {
      window.clearTimeout(delayTimer);
    };
  }, [entry?.isIntersecting, delay]);

  return {
    ref,
    entry,
    delayedIntersection,
  };
};
